import { Check, ExternalLink } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { JarvisOrb } from '@/components/JarvisOrb'
import { JarvisCard } from '@/components/JarvisCard'
import { useCurrentUser } from '@/lib/hooks'
import { apiClient, unwrap } from '@/lib/apiClient'
import { usePersona } from '@/lib/personas'
import { cn } from '@/lib/cn'

interface Tier {
  name: string
  priceId: string
  price: string
  features: string[]
  highlight: boolean
}

const TIERS: Tier[] = [
  {
    name: 'Starter',
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_STARTER,
    price: '$49/mo',
    features: [
      'Voice agent — inbound calls',
      'Facebook Ads intelligence',
      '100 call minutes/month',
      'Google Calendar booking',
      'CRM + follow-up emails',
    ],
    highlight: false,
  },
  {
    name: 'Pro',
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_PRO,
    price: '$149/mo',
    features: [
      'Everything in Starter',
      'Morning briefing via voice call to owner',
      'Unlimited call minutes',
      'Morning briefings',
      'Competitor ad intelligence',
      'Priority support',
    ],
    highlight: true,
  },
  {
    name: 'Enterprise',
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_ENTERPRISE,
    price: 'Talk to us',
    features: [
      'Everything in Pro',
      'Custom Jarvis voice clone',
      'Custom integrations',
      'Dedicated onboarding',
      'SLA guarantee',
    ],
    highlight: false,
  },
]

export function Billing() {
  const { data: user } = useCurrentUser()
  const { persona } = usePersona()

  const checkout = useMutation({
    mutationFn: async (priceId: string) => {
      const { url } = await unwrap<{ url: string }>(
        apiClient.post('/billing/checkout', { priceId }),
      )
      window.location.href = url
    },
  })

  const portal = useMutation({
    mutationFn: async () => {
      const { url } = await unwrap<{ url: string }>(apiClient.post('/billing/portal'))
      window.location.href = url
    },
  })

  if (user?.jarvisEnabled) {
    return (
      <div className="space-y-6 max-w-2xl">
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <JarvisCard>
          <div className="space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-lg font-medium">
                {persona.wordmark}{' '}
                {user.subscriptionTier?.replace(/^./, (c) => c.toUpperCase())}
              </h2>
              <span className="inline-flex items-center gap-1.5 text-xs text-jarvis-green">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-jarvis-green" />
                {user.subscriptionStatus}
              </span>
            </div>
            {user.trialEndsAt && (
              <p className="text-sm text-jarvis-muted">
                Trial ends {new Date(user.trialEndsAt).toLocaleDateString()}
              </p>
            )}
            <button
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
              className="mt-2 inline-flex items-center gap-2 bg-jarvis-blue hover:bg-jarvis-blue-bright disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Manage Billing <ExternalLink size={14} />
            </button>
          </div>
        </JarvisCard>
      </div>
    )
  }

  return (
    <div className="space-y-10 max-w-5xl mx-auto py-6">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <JarvisOrb state="idle" size="md" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{persona.wordmark}</h1>
        <p className="text-jarvis-muted text-sm uppercase tracking-widest">
          {persona.tagline}
        </p>
        <p className="text-jarvis-muted">
          Choose a plan to activate {persona.wordmark}. Your AI goes live immediately. Cancel any time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TIERS.map((tier) => (
          <JarvisCard
            key={tier.name}
            className={cn(
              'flex flex-col',
              tier.highlight && 'ring-1 ring-jarvis-blue/40 shadow-lg shadow-jarvis-blue/10',
            )}
          >
            {tier.highlight && (
              <span className="self-start text-xs uppercase tracking-widest text-jarvis-blue mb-2">
                ★ Most Popular
              </span>
            )}
            <h3 className="text-xl font-semibold">{tier.name}</h3>
            <p className="text-2xl font-bold mt-2 tabular">{tier.price}</p>
            <ul className="mt-6 space-y-2.5 flex-1">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-jarvis-blue mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => tier.priceId && checkout.mutate(tier.priceId)}
              disabled={!tier.priceId || checkout.isPending}
              className={cn(
                'mt-6 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
                tier.highlight
                  ? 'bg-jarvis-blue hover:bg-jarvis-blue-bright'
                  : 'bg-jarvis-surface-2 hover:bg-jarvis-surface border border-jarvis-border',
              )}
            >
              {tier.priceId ? 'Get Started' : 'Configure Stripe'}
            </button>
          </JarvisCard>
        ))}
      </div>
    </div>
  )
}
